import { useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { ScheduleItem } from '../api/types';
import { Alert, LoadingBlock } from '../components/ui';
import { dagsoverskrift, etterDag } from '../lib/programdager';
import '../styles/program.css';

type Svar = { items: ScheduleItem[] };

/**
 * Programmet slik gjesten ser det.
 *
 * Samme tidslinje som brudeparet lager, uten knapper. Klokkeslettene står på
 * linje til venstre, så det er til å lese nedover mens man leter etter når
 * middagen er.
 */
export function Dagsprogram({
  token,
  weddingDate,
  forhandsvisningFor,
}: {
  token: string;
  weddingDate: string | null;
  /** Satt når brudeparet ser på sin egen forhåndsvisning, uten svarlenke. */
  forhandsvisningFor?: string;
}) {
  const [items, setItems] = useState<ScheduleItem[] | null>(null);
  const [feil, setFeil] = useState<string | null>(null);

  useEffect(() => {
    let gjelder = true;
    api
      .get<Svar>(
        forhandsvisningFor ? `/weddings/${forhandsvisningFor}/program` : `/rsvp/${token}/program`,
      )
      .then((svar) => gjelder && setItems(svar.items))
      .catch((caught) => {
        if (!gjelder) return;
        // Et par på Gratis har ikke verktøyet, og da har de heller ikke noe
        // program. I forhåndsvisningen er det riktigere å si det enn å vise
        // dem en feilmelding om noe de ikke har slått på.
        if (forhandsvisningFor && caught instanceof ApiError && caught.isPlanRequired) {
          setItems([]);
          return;
        }
        setFeil(caught instanceof ApiError ? caught.message : 'Klarte ikke å hente programmet.');
      });
    return () => {
      gjelder = false;
    };
  }, [token, forhandsvisningFor]);

  if (feil) return <Alert kind="error">{feil}</Alert>;
  if (!items) return <LoadingBlock label="Henter programmet …" />;

  if (items.length === 0) {
    return (
      <p className="muted" style={{ textAlign: 'center' }}>
        Brudeparet har ikke lagt ut noe program ennå. Se innom igjen senere.
      </p>
    );
  }

  return (
    <div className="program-gjest">
      {etterDag(items).map(([avvik, punkter]) => (
        <section key={avvik}>
          <h2 className="program-dag">
            {dagsoverskrift(avvik, weddingDate).tittel}
            {dagsoverskrift(avvik, weddingDate).merke ? (
              <span className="program-dato">{dagsoverskrift(avvik, weddingDate).merke}</span>
            ) : null}
          </h2>
          <ol className="program-liste">
            {punkter.map((item) => (
              <li className="program-punkt" key={item.id}>
                <div className="program-klokke">
                  {item.startsAt ?? <span className="muted">–</span>}
                </div>
                <div className="program-innhold">
                  <h3>{item.title}</h3>
                  {item.location ? <p className="program-sted">{item.location}</p> : null}
                  {item.notes ? <p className="small muted">{item.notes}</p> : null}
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
